import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostHog } from '@posthog/react';
import useGenerateItinerary, { GenerateItineraryRequestError } from '@/api/itineraries/useGenerateItinerary.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { useToast } from '@/context/ToastContext.tsx';
import { queryClient } from '@/main.tsx';
import Routes from '@/router/Routes.ts';
import { passToRoute } from '@/utils/passToRoute.ts';

const HTTP_CONFLICT = 409;

const useRetryTripGeneration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const posthog = usePostHog();
  const { getSubscriptionPage } = useAuthContext();
  const { addToast } = useToast();
  const { mutateAsync: generateItinerary } = useGenerateItinerary();
  const [retryingTripId, setRetryingTripId] = useState<string | null>(null);

  const openTrip = useCallback(
    (tripId: string) => {
      const tripPath = passToRoute(Routes.TRIP, { id: tripId });

      if (pathname !== tripPath) navigate(tripPath);
    },
    [navigate, pathname]
  );

  const retry = useCallback(
    async (tripId: string) => {
      if (retryingTripId) return;

      setRetryingTripId(tripId);

      try {
        await generateItinerary({ trip_id: tripId });
        openTrip(tripId);
      } catch (error) {
        if (error instanceof Error && error.message === 'limit') {
          navigate(getSubscriptionPage());
          return;
        }

        if (error instanceof GenerateItineraryRequestError && error.status === HTTP_CONFLICT) {
          await queryClient.invalidateQueries({ queryKey: ['itineraries'] });
          openTrip(tripId);
          return;
        }

        posthog?.captureException(error, { operation: 'retry_itinerary_generation', trip_id: tripId });
        addToast(error instanceof Error && error.message ? error.message : t('toast.retry_failed'), 'error');
      } finally {
        setRetryingTripId(null);
      }
    },
    [addToast, generateItinerary, getSubscriptionPage, navigate, openTrip, posthog, retryingTripId, t]
  );

  return { retry, retryingTripId };
};

export default useRetryTripGeneration;
